import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCoreRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Columns3, FilterX, MoreVertical, Plus, RefreshCw, Search } from "lucide-react";
import { useMemo, useState } from "react";

import AuthorizedButton from "@/components/common/AuthorizedButton";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import EdgeScrollButton from "@/components/common/EdgeScrollButton";
import PageHeader from "@/components/common/PageHeader";
import Paginator from "@/components/common/Paginator";
import SingleFieldEditDialog, {
  type SingleFieldTarget,
} from "@/components/common/SingleFieldEditDialog";
import ColumnSettingsButton from "@/components/table/ColumnSettingsButton";
import DataTable from "@/components/table/DataTable";
import { useHorizontalScroll } from "@/hooks/useHorizontalScroll";
import { useTableSettings } from "@/hooks/useTableSettings";
import { errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { canWrite } from "@/lib/authorization";
import { showError, showSuccess } from "@/lib/toast";
import {
  deleteProcess,
  listProcesses,
  PROCESS_CATEGORY_LABEL,
  PROCESS_CATEGORY_OPTIONS,
  updateProcess,
  type Process,
  type ProcessInput,
} from "./api";
import {
  buildProcessColumns,
  processColumnIds,
  processPinnedColumnIds,
  type ProcessField,
} from "./columns";
import ProcessCreateDialog from "./ProcessCreateDialog";
import { PROCESSES_PAGE_SIZE, useProcessListParams } from "./useProcessListParams";

const USAGE_OPTIONS = [
  { key: "all", label: "すべて" },
  { key: "active", label: "使用中" },
  { key: "stopped", label: "使用停止" },
];

/** 費目の絞り込み。工程を費目別に見たいときに使う。 */
const CATEGORY_OPTIONS = [{ key: "all", label: "すべて" }, ...PROCESS_CATEGORY_OPTIONS];

/**
 * 工程マスタ。
 *
 * 見積明細の金額は、ここに登録した工程ごとの見積の合算で作る。時間単価は
 * 「見積で時間を入れたときの既定値」でしかなく、**変更しても過去の見積金額は
 * 動かない**（見積側が採用時の単価と金額を控えているため）。
 *
 * 画面の作りは取引先マスタ（[[PartnerList]]）と同じ。セルをクリックして 1 項目だけ
 * 編集し、ダイアログはこの画面が 1 つずつ持って対象だけ差し替える。
 */
export default function ProcessList() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const editable = canWrite(user, "master");
  const {
    params,
    update,
    reset: resetParams,
    disabledParam,
    categoryParam,
  } = useProcessListParams();
  const {
    settings,
    setColumnVisibility,
    setColumnOrder,
    setColumnSizing,
    reset: resetColumns,
  } = useTableSettings("processes", processColumnIds, processPinnedColumnIds);
  const { scrollRef, scrollBy, canScrollLeft, canScrollRight } = useHorizontalScroll();

  const [creating, setCreating] = useState(false);
  // 編集中の 1 項目（セルをクリックすると入る）。
  const [editing, setEditing] = useState<{
    process: Process;
    field: ProcessField;
  } | null>(null);
  const [toggling, setToggling] = useState<Process | null>(null);
  const [deleting, setDeleting] = useState<Process | null>(null);

  const query = useQuery({
    queryKey: ["processes", params],
    queryFn: () =>
      listProcesses({
        keyword: params.keyword || undefined,
        disabled: disabledParam,
        category: categoryParam,
        sortKey: params.sortKey,
        ascending: params.ascending,
        limit: PROCESSES_PAGE_SIZE,
        offset: (params.page - 1) * PROCESSES_PAGE_SIZE,
      }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["processes"] });

  const save = async (
    process: Process,
    input: ProcessInput,
  ): Promise<string | null> => {
    try {
      await updateProcess(process.process_id, input);
      await invalidate();
      return null;
    } catch (e) {
      // 入力し直せば解決する失敗（重複コードなど）はダイアログ内に返す。
      return errorMessage(e, "保存に失敗しました。");
    }
  };

  const toggle = useMutation({
    mutationFn: (process: Process) =>
      updateProcess(process.process_id, { disabled: !process.disabled }),
    onSuccess: (process) => {
      showSuccess(
        `「${process.process_name}」を${process.disabled ? "使用停止" : "使用中"}にしました。`,
      );
      void invalidate();
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setToggling(null),
  });

  const remove = useMutation({
    mutationFn: (process: Process) => deleteProcess(process.process_id),
    onSuccess: (_, process) => {
      showSuccess(`「${process.process_name}」を削除しました。`);
      void invalidate();
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setDeleting(null),
  });

  const columns = useMemo(
    () =>
      buildProcessColumns({
        canWrite: editable,
        onEditField: (process, field) => setEditing({ process, field }),
        onToggleDisabled: setToggling,
        onDelete: setDeleting,
      }),
    [editable],
  );

  // 並び替えは API 側で行う（manualSorting）。列定義の meta.sortKey を使う。
  const sorting: SortingState = [{ id: params.sortKey, desc: !params.ascending }];

  const table = useReactTable<Process>({
    data: query.data?.items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.process_id),
    columnResizeMode: "onChange",
    manualSorting: true,
    manualPagination: true,
    enableSortingRemoval: false,
    state: {
      sorting,
      columnVisibility: settings.columnVisibility,
      columnOrder: settings.columnOrder,
      columnSizing: settings.columnSizing,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      const first = next[0];
      if (!first) return;
      const sortKey = table.getColumn(first.id)?.columnDef.meta?.sortKey;
      if (!sortKey) return;
      update({ sortKey, ascending: !first.desc });
    },
    enableSorting: true,
    defaultColumn: { enableSorting: false, minSize: 60, maxSize: 600 },
  });

  // 列ごとの並び替え可否は meta.sortKey の有無で決まる。
  for (const column of table.getAllLeafColumns()) {
    column.columnDef.enableSorting = Boolean(column.columnDef.meta?.sortKey);
  }

  const total = query.data?.pagination.total ?? 0;

  const editTarget: SingleFieldTarget | null = editing
    ? {
        label: editing.field.label,
        value: (editing.process[editing.field.key] as string | null) ?? null,
        required: editing.field.required,
        kind: editing.field.kind ?? "text",
        options: editing.field.options,
        description: editing.field.description,
      }
    : null;

  return (
    <div className="relative h-full">
      <div className="flex h-full flex-col px-6 pt-3">
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-md">
          <PageHeader
            title="工程"
            actions={
              <>
                <Input
                  aria-label="工程コード・工程名で検索"
                  placeholder="コード・工程名"
                  variant="bordered"
                  radius="sm"
                  className="w-60"
                  startContent={<Search size={16} className="text-default-400" />}
                  defaultValue={params.keyword}
                  onBlur={(e) =>
                    update({ keyword: (e.target as HTMLInputElement).value.trim() })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      update({ keyword: (e.target as HTMLInputElement).value.trim() });
                    }
                  }}
                  isClearable
                  onClear={() => update({ keyword: "" })}
                />
                <Select
                  aria-label="使用状態で絞り込む"
                  variant="bordered"
                  radius="sm"
                  className="w-44"
                  disallowEmptySelection
                  selectedKeys={[params.usage]}
                  onSelectionChange={(keys) => {
                    const value = [...keys][0] as string | undefined;
                    if (value === "active" || value === "stopped" || value === "all") {
                      update({ usage: value });
                    }
                  }}
                  renderValue={(items) => {
                    const key = items[0]?.key as string | undefined;
                    const label =
                      USAGE_OPTIONS.find((o) => o.key === key)?.label ?? "すべて";
                    return `使用：${label}`;
                  }}
                  classNames={{
                    trigger: params.usage !== "all" ? "border-brand-500" : "",
                    value: params.usage !== "all" ? "font-bold text-brand-500" : "",
                  }}
                >
                  {USAGE_OPTIONS.map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>
                <Select
                  aria-label="費目で絞り込む"
                  variant="bordered"
                  radius="sm"
                  className="w-48"
                  disallowEmptySelection
                  selectedKeys={[params.category]}
                  onSelectionChange={(keys) => {
                    const value = [...keys][0] as string | undefined;
                    if (value === "all") {
                      update({ category: "all" });
                    } else if (value && value in PROCESS_CATEGORY_LABEL) {
                      update({
                        category: value as keyof typeof PROCESS_CATEGORY_LABEL,
                      });
                    }
                  }}
                  renderValue={(items) => {
                    const key = items[0]?.key as string | undefined;
                    const label =
                      CATEGORY_OPTIONS.find((o) => o.key === key)?.label ?? "すべて";
                    return `費目：${label}`;
                  }}
                  classNames={{
                    trigger: params.category !== "all" ? "border-brand-500" : "",
                    value: params.category !== "all" ? "font-bold text-brand-500" : "",
                  }}
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>
                <AuthorizedButton
                  scope="master"
                  radius="full"
                  color="primary"
                  data-testid="process-create"
                  startContent={<Plus size={18} />}
                  onPress={() => setCreating(true)}
                >
                  新規登録
                </AuthorizedButton>
                <ColumnSettingsButton table={table} onReset={resetColumns} />

                <Dropdown>
                  <DropdownTrigger>
                    <Button
                      isIconOnly
                      variant="light"
                      radius="full"
                      aria-label="表示設定"
                    >
                      <MoreVertical size={20} className="text-brand-500" />
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="表示設定"
                    onAction={(key) => {
                      if (key === "reload") void query.refetch();
                      if (key === "reset_filters") resetParams();
                      if (key === "reset_columns") resetColumns();
                    }}
                  >
                    <DropdownItem key="reload" startContent={<RefreshCw size={18} />}>
                      再読み込み
                    </DropdownItem>
                    <DropdownItem
                      key="reset_filters"
                      startContent={<FilterX size={18} />}
                    >
                      検索条件リセット
                    </DropdownItem>
                    <DropdownItem
                      key="reset_columns"
                      startContent={<Columns3 size={18} />}
                    >
                      表示列リセット
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </>
            }
          />

          <div className="min-h-0 flex-1">
            {query.isError ? (
              <div className="flex h-full flex-col items-center justify-center gap-4">
                <p className="text-default-600">
                  データの取得に失敗しました。
                  <br />
                  {(query.error as Error).message}
                </p>
                <Button color="primary" onPress={() => void query.refetch()}>
                  再読み込み
                </Button>
              </div>
            ) : query.isPending ? (
              <div className="flex h-full items-center justify-center">
                <Spinner label="読み込み中..." />
              </div>
            ) : (
              <DataTable
                table={table}
                isLoading={query.isFetching}
                emptyMessage="工程がありません。「新規登録」から追加してください。"
                scrollRef={scrollRef}
              />
            )}
          </div>
        </section>

        <Paginator
          total={total}
          pageSize={PROCESSES_PAGE_SIZE}
          page={params.page}
          onChange={(page) => update({ page })}
        />
      </div>

      <EdgeScrollButton
        side="left"
        isDisabled={!canScrollLeft}
        onPress={() => scrollBy(-1)}
      />
      <EdgeScrollButton
        side="right"
        isDisabled={!canScrollRight}
        onPress={() => scrollBy(1)}
      />

      <ProcessCreateDialog
        isOpen={creating}
        onClose={() => setCreating(false)}
        onCreated={(name) => {
          showSuccess(`「${name}」を登録しました。`);
          void invalidate();
        }}
      />

      <SingleFieldEditDialog
        target={editTarget}
        onClose={() => setEditing(null)}
        onSave={(value) =>
          editing
            ? save(editing.process, { [editing.field.key]: value } as ProcessInput)
            : Promise.resolve(null)
        }
      />

      <ConfirmDialog
        isOpen={toggling != null}
        title={toggling?.disabled ? "使用の再開" : "使用の停止"}
        message={
          toggling?.disabled
            ? `「${toggling.process_name}」を使用中に戻します。見積の工程候補に再び表示されます。`
            : `「${toggling?.process_name}」を使用停止にします。\n見積の工程候補から外れますが、過去の見積の内訳と金額はそのまま残ります。`
        }
        confirmLabel={toggling?.disabled ? "使用を再開" : "使用を停止"}
        isBusy={toggle.isPending}
        onClose={() => setToggling(null)}
        onConfirm={() => {
          if (toggling) toggle.mutate(toggling);
        }}
      />

      <ConfirmDialog
        isOpen={deleting != null}
        title="工程の削除"
        message={`「${deleting?.process_name}」を削除します。\nこの操作は取り消せません。よろしいですか？\n\n見積の内訳で使われている工程は削除できません（消すと過去の見積金額が変わってしまうため）。使わなくなっただけなら「使用停止」にしてください。`}
        confirmLabel="削除"
        isDanger
        isBusy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
        }}
      />
    </div>
  );
}
